import { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "../../lib/callable";
import { Loader2, X, FileCheck2 } from "lucide-react";
import { functions } from "../../firebase";
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from "../../constants/firebase";
import {
  buildVersionedFilename,
  type VersionedFilenameInput,
} from "../../utils/versionedFilename";
import { nextMinorVersion, nextMajorVersion } from "../../types/issuedVersion";
import type { SourceDocFamily } from "../../hooks/useIssuedVersions";

export interface IssueVersionModalProps {
  isOpen: boolean;
  onClose: () => void;

  // Source identification
  projectId: string;
  sourceDocFamily: SourceDocFamily;
  sourceDocId: string;
  docCode: string;
  docName: string; // Used in description, not the filename

  // Filename context (for live preview)
  shortName: string | null | undefined;
  protocolNumber?: string | null;
  fileExtension?: string;

  // Current state
  currentVersion: string | null | undefined;

  // Caller renders the DOCX bytes — we don't dispatch by doc type here.
  // Omit for a document the server renders itself AFTER the version is
  // assigned (the DMP): a browser render happens before, and is a snapshot of
  // the draft.
  renderDocx?: () => Promise<{ docxBase64: string }>;

  // Result handling
  onSuccess?: (result: IssueVersionResult) => void;
}

export interface IssueVersionResult {
  versionId: string;
  versionString: string;
  filename: string;
  storagePath: string;
  bytes: number;
}

type RevisionChoice = "minor" | "major";

export function IssueVersionModal(props: IssueVersionModalProps) {
  const {
    isOpen,
    onClose,
    projectId,
    sourceDocFamily,
    sourceDocId,
    docCode,
    docName,
    shortName,
    protocolNumber,
    fileExtension = "docx",
    currentVersion,
    renderDocx,
    onSuccess,
  } = props;

  const isFirstIssue = !currentVersion;
  const [revisionChoice, setRevisionChoice] = useState<RevisionChoice>("minor");
  const [changeDescription, setChangeDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset when opening
  useEffect(() => {
    if (isOpen) {
      setRevisionChoice("minor");
      setChangeDescription("");
      setErrorMessage(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const targetVersionString = isFirstIssue
    ? "1.0"
    : revisionChoice === "minor"
    ? nextMinorVersion(currentVersion)
    : nextMajorVersion(currentVersion);

  const filenamePreview = useMemo(() => {
    const [majorStr, minorStr] = targetVersionString.split(".");
    const input: VersionedFilenameInput = {
      shortName,
      protocolNumber,
      docCode,
      major: Number(majorStr),
      minor: Number(minorStr),
      issueDate: new Date(),
      extension: fileExtension,
    };
    return buildVersionedFilename(input);
  }, [
    targetVersionString,
    shortName,
    protocolNumber,
    docCode,
    fileExtension,
  ]);

  if (!isOpen) return null;

  const trimmedDescription = changeDescription.trim();
  const canSubmit =
    trimmedDescription.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const rendered = renderDocx ? await renderDocx() : null;
      const docxBase64 = rendered?.docxBase64;
      if (renderDocx && !docxBase64) {
        throw new Error("Failed to render document for issuing.");
      }
      const issueFn = httpsCallable(functions, CLOUD_FUNCTIONS.ISSUE_VERSION, { timeout: CALLABLE_TIMEOUTS.SHORT });
      const response = await issueFn({
        projectId,
        sourceDocFamily,
        sourceDocId,
        docCode,
        ...(docxBase64 ? { docxBase64 } : {}),
        revisionType: isFirstIssue ? "minor" : revisionChoice,
        changeDescription: trimmedDescription,
        fileExtension,
      });
      const data = response.data as IssueVersionResult;
      if (onSuccess) onSuccess(data);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "An unexpected error occurred while issuing the version.";
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 flex flex-col">
        <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-50 ring-1 ring-teal-200 flex items-center justify-center">
              <FileCheck2 className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Approve &amp; Issue v{targetVersionString}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">{docName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {!isFirstIssue && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Issuing as
              </label>
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="radio"
                    name="revisionChoice"
                    value="minor"
                    checked={revisionChoice === "minor"}
                    onChange={() => setRevisionChoice("minor")}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      Minor revision (v{nextMinorVersion(currentVersion)})
                    </div>
                    <div className="text-xs text-slate-500">
                      Small changes — typo fixes, clarifications, formatting.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="radio"
                    name="revisionChoice"
                    value="major"
                    checked={revisionChoice === "major"}
                    onChange={() => setRevisionChoice("major")}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">
                      Major revision (v{nextMajorVersion(currentVersion)})
                    </div>
                    <div className="text-xs text-slate-500">
                      Substantive changes that warrant re-review.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          <div>
            <label
              htmlFor="changeDescription"
              className="block text-sm font-medium text-slate-700 mb-2"
            >
              What changed in this revision? <span className="text-red-500">*</span>
            </label>
            <textarea
              id="changeDescription"
              rows={3}
              value={changeDescription}
              onChange={(e) => setChangeDescription(e.target.value)}
              placeholder="Brief summary auditors will see in the version history..."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                         disabled:bg-slate-50 disabled:text-slate-500"
              disabled={submitting}
            />
            {trimmedDescription.length === 0 && (
              <p className="text-xs text-slate-500 mt-1">
                Required. This is the entry that will appear in the History row.
              </p>
            )}
          </div>

          <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs">
            <span className="text-slate-500">Filename: </span>
            <span className="font-mono text-slate-700 break-all">
              {filenamePreview}
            </span>
          </div>

          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Issue v{targetVersionString}
          </button>
        </div>
      </div>
    </div>
  );
}
